import axios, {AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios';
import {DateTime} from "luxon";

export class Service {

    private readonly axiosInstance: AxiosInstance;
    private config: AxiosRequestConfig;
    private dateFormat = /^\d{4}-\d{2}-\d{2}$/;
    private datetimeFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}[+-]\d{2}:\d{2}$/;
    private luxonFormat = "yyyy'-'LL'-'dd'T'HH':'mm':'ss'.'SSSZZ";

    constructor(config?: AxiosRequestConfig) {
        this.config = config;
        this.axiosInstance = axios.create(config);

        this.axiosInstance.interceptors.request.use(
            request => {
                this.parseDateTimes(request.data);
                return request;
            }
        );
        this.axiosInstance.interceptors.response.use(response => {
            this.parseDates(response.data);
            return response;
        });
    }

    private isDateString(value: any): boolean {
        return value && typeof value === "string" && (this.dateFormat.test(value) || this.datetimeFormat.test(value));
    }

    public parseDate(value: any): Date {
        if (this.dateFormat.test(value)) {
            return DateTime.fromISO(value).toLocal();
        }
        if (this.datetimeFormat.test(value)) {
            return DateTime.fromISO(value).toLocal();
        }
        return value;
    }

    public parseDates(body: any) {
        if (body === null || body === undefined || typeof body !== "object") {
            return;
        }
        for (const key of Object.keys(body)) {
            const value = body[key];
            if (this.isDateString(value)) {
                body[key] = this.parseDate(value);
            } else if (typeof value === "object") {
                this.parseDates(value);
            }
        }
    }

    public parseDateTime(value: DateTime): string {
        const td = DateTime.fromObject(value.c, {zone: value._zone.zoneName});
        return td.toFormat(this.luxonFormat);
    }

    public parseDateTimes(body: any) {
        if (body === null || body === undefined || typeof body !== "object") {
            return;
        }
        for (const key of Object.keys(body)) {
            const value = body[key];
            if (DateTime.isDateTime(value)) {
                body[key] = this.parseDateTime(value);
            } else if (typeof value === "object") {
                this.parseDateTimes(value);
            }
        }
    }

    protected getAxiosInstance = (): AxiosInstance => {
        return this.axiosInstance;
    }

    protected request = async <T>(
        config: AxiosRequestConfig,
    ): Promise<AxiosResponse<T>> => {
        return await this.axiosInstance.request(config);
    };

    protected get = async <T>(
        url: string,
        config?: AxiosRequestConfig,
    ): Promise<AxiosResponse<T>> => {
        return await this.axiosInstance.get<T>(url, config);
    };

    protected post = async <T>(
        url: string,
        body?: any,
        config?: AxiosRequestConfig,
    ): Promise<AxiosResponse<T>> => {
        return await this.axiosInstance.post(url, body, config);
    };

    protected put = async <T>(
        url: string,
        body?: any,
        config?: AxiosRequestConfig,
    ): Promise<AxiosResponse<T>> => {
        return await this.axiosInstance.put(url, body, config);
    };

    protected delete = async <T>(
        url: string,
        config?: AxiosRequestConfig,
    ): Promise<AxiosResponse<T>> => {
        return await this.axiosInstance.delete<T>(url, config);
    };

    public data = <T>(response: AxiosResponse<T>): T => {
        if (response != null && response.status == 200) {
            return response.data;
        }
        return null;
    };
}
